import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CrmClient } from '../crm/crm.model';
import { EquipmentCategory, EquipmentRateCard, EquipmentResource } from './field-work.models';

@Component({
  selector: 'app-equipment-rate-create-dialog',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule
  ],
  templateUrl: './equipment-rate-create-dialog.component.html',
  styleUrl: './equipment-rate-create-dialog.component.css'
})
export class EquipmentRateCreateDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() saving = false;
  @Input() error = '';
  @Input() resources: EquipmentResource[] = [];
  @Input() categories: EquipmentCategory[] = [];
  @Input() customers: CrmClient[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() create = new EventEmitter<EquipmentRateCard>();

  form: EquipmentRateCard = this.emptyForm();
  validationError = '';

  readonly ownershipOptions = [
    { label: 'Owned equipment', value: 'OWNED' },
    { label: 'Rented equipment', value: 'RENTAL' }
  ];
  readonly unitOptions = [
    { label: 'Per hour', value: 'HOUR' },
    { label: 'Per day', value: 'DAY' },
    { label: 'Per task', value: 'TASK' },
    { label: 'Flat charge', value: 'FLAT' }
  ];
  readonly billableOptions = [
    { label: 'No — cost only', value: false },
    { label: 'Yes — customer charge', value: true }
  ];
  readonly activityOptions = [
    'GENERAL', 'LOADING', 'UNLOADING', 'INSTALLATION', 'REMOVAL', 'RETURN', 'PACKING', 'INSPECTION'
  ];
  readonly targetOptions = [
    'SHIPMENT', 'SHIPMENT_DELIVERY', 'TRANSPORT_ORDER_RELEASE', 'POSM_DELIVERY_SCHEDULE',
    'POSM_INSTALLATION_TASK', 'POSM_REMOVAL_TASK', 'CONTAINER_CASE', 'CONTAINER_JOURNEY_ITEM',
    'WAREHOUSE', 'WAREHOUSE_TRANSFER', 'WAREHOUSE_DISPATCH', 'CUSTOMS_CLEARANCE', 'LOGISTICS_PROJECT'
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) this.reset();
  }

  get resourceOptions(): Array<{ label: string; value: number }> {
    return this.resources.filter(row => Boolean(row.equipmentResourceId)).map(row => ({
      label: `${row.resourceName || row.resourceCode}${row.resourceCode ? ` — ${row.resourceCode}` : ''}`,
      value: row.equipmentResourceId!
    }));
  }

  get categoryOptions(): Array<{ label: string; value: number }> {
    return this.categories.filter(row => row.active !== false && Boolean(row.equipmentCategoryId)).map(row => ({
      label: `${row.categoryName || row.categoryCode}${row.categoryCode ? ` — ${row.categoryCode}` : ''}`,
      value: row.equipmentCategoryId!
    }));
  }

  get customerOptions(): Array<{ label: string; value: number }> {
    return this.customers.filter(row => Boolean(row.id)).map(row => ({
      label: `${row.companyName || row.clientNumber}${row.clientNumber ? ` — ${row.clientNumber}` : ''}`,
      value: row.id!
    }));
  }

  get scopeSummary(): string {
    const resource = this.resources.find(row => row.equipmentResourceId === this.form.equipmentResourceId);
    if (resource) return resource.resourceName || resource.resourceCode || 'Specific resource';
    const category = this.categories.find(row => row.equipmentCategoryId === this.form.equipmentCategoryId);
    if (category) return category.categoryName || category.categoryCode || 'Equipment category';
    if (this.form.ownershipType) return this.form.ownershipType === 'RENTAL' ? 'Rented equipment' : 'Owned equipment';
    return 'Tenant default';
  }

  resourceChanged(resourceId?: number): void {
    const resource = this.resources.find(row => row.equipmentResourceId === resourceId);
    if (!resource) return;
    this.form.equipmentCategoryId = resource.equipmentCategoryId;
    this.form.ownershipType = resource.ownershipType;
  }

  reset(): void {
    this.form = this.emptyForm();
    this.validationError = '';
  }

  close(): void {
    if (!this.saving) this.visibleChange.emit(false);
  }

  submit(): void {
    this.validationError = '';
    if (!this.form.rateCode?.trim() || !this.form.rateName?.trim() || !this.form.effectiveFrom) {
      this.validationError = 'Rate code, name, and effective-from date are required.';
      return;
    }
    if (this.form.buyRate == null && this.form.sellRate == null) {
      this.validationError = 'Enter a buy rate, a sell rate, or both.';
      return;
    }
    if (this.form.billable && (this.form.sellRate == null || this.form.sellRate < 0)) {
      this.validationError = 'A billable rate card requires a valid sell rate.';
      return;
    }
    if (this.form.effectiveUntil && new Date(this.form.effectiveUntil) < new Date(this.form.effectiveFrom)) {
      this.validationError = 'Effective-until date cannot be earlier than effective-from.';
      return;
    }
    this.create.emit({
      ...this.form,
      rateCode: this.form.rateCode.trim().toUpperCase(),
      rateName: this.form.rateName.trim(),
      currencyCode: (this.form.currencyCode || 'SAR').trim().toUpperCase(),
      productCodeSnapshot: (this.form.productCodeSnapshot || 'SHP-EQUIPMENT').trim(),
      effectiveFrom: this.dateOnly(this.form.effectiveFrom),
      effectiveUntil: this.dateOnly(this.form.effectiveUntil)
    });
  }

  private dateOnly(value?: string | Date): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return value.slice(0, 10);
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private emptyForm(): EquipmentRateCard {
    return {
      rateCode: '', rateName: '', billingUnit: 'DAY', currencyCode: 'SAR', taxRate: 0,
      billable: false, productCodeSnapshot: 'SHP-EQUIPMENT', effectiveFrom: new Date(),
      priority: 0, active: true
    };
  }
}
